<template>
    <div
        ref="rootRef"
        class="ui-control"
        :class="{ 'ui-control--block': block }"
    >
        <span v-if="label" class="ui-control__label">{{ label }}</span>
        <button
            type="button"
            class="ui-control__shell ui-control__trigger"
            :id="id"
            :class="{
                'is-open': open,
                'is-disabled': disabled,
                'is-error': !!error,
            }"
            :disabled="disabled"
            :aria-label="label || undefined"
            :aria-expanded="open"
            aria-haspopup="listbox"
            @click.stop="toggle"
        >
            <span class="ui-control__value">
                <slot name="summary">{{ summary }}</slot>
            </span>
            <svg class="ui-control__arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>

        <div
            v-if="open"
            class="ui-control__dropdown"
            :class="{
                'ui-control__dropdown--wide': wide,
                'ui-control__dropdown--left': align === 'left',
                'ui-control__dropdown--block': block,
            }"
            role="listbox"
            @click.stop
        >
            <slot />
        </div>

        <div v-if="error" class="ui-control__error">
            <p>{{ error }}</p>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    label: { type: String, default: '' },
    summary: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    wide: { type: Boolean, default: false },
    align: { type: String, default: 'right' },
    error: { type: String, default: '' },
    id: { type: String, default: undefined },
    modelValue: { type: Boolean, default: undefined },
});

const emit = defineEmits(['update:modelValue', 'open', 'close']);

const rootRef = ref(null);
const unmanagedOpen = ref(false);

const isControlled = computed(() => props.modelValue !== undefined);

const open = computed({
    get() {
        return isControlled.value ? !!props.modelValue : unmanagedOpen.value;
    },
    set(value) {
        if (isControlled.value) {
            emit('update:modelValue', value);
        } else {
            unmanagedOpen.value = value;
        }
    },
});

function setOpen(value) {
    if (props.disabled) {
        return;
    }

    const next = !!value;
    if (open.value === next) {
        return;
    }

    open.value = next;

    if (next) {
        emit('open');
    } else {
        emit('close');
    }
}

function openDropdown() {
    setOpen(true);
}

function close() {
    setOpen(false);
}

function toggle() {
    setOpen(!open.value);
}

function handleClickOutside(event) {
    if (rootRef.value && !rootRef.value.contains(event.target)) {
        close();
    }
}

function handleKeydown(event) {
    if (event.key === 'Escape' && open.value) {
        close();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleKeydown);
});

defineExpose({ open: openDropdown, close, toggle, rootRef });
</script>
